import type { Category, Product } from '@agos/api-client/model'
import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

import { UNCATEGORISED_ADDONS } from '../products/addon-groups'
import type {
  CopyMode,
  UpsellCopyPayload,
  UpsellGroup,
  UpsellGroupPayload,
  UpsellItem,
  UpsellLinksPayload,
} from './upsell-api'

/**
 * The upsell screen's rules - `FRONTEND.md` §9.711. Pure, so every one of them is tested without a screen.
 *
 * Nothing here decides what the server will store; the server refuses a blank name, a limit below one and a
 * negative price in Slovak (§10A.365), and the screen shows that sentence. These rules exist so the screen does
 * not **send** a value it could only have guessed - an empty price box is *the item's own price*, not nought.
 */

// ─── Order ───────────────────────────────────────────────────────────────────

/**
 * The list with the entry at `from` moved to `to` - the drop of a drag.
 *
 * Out-of-range indexes answer the list unchanged (a copy), so a drop outside the list or a drag that never
 * started cannot reorder anything.
 */
export function moveTo<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list]
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return next
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved as T)
  return next
}

/** One step up (`-1`) or down (`+1`) - the keyboard and touch fallback for the drag. */
export function moveBy<T>(list: readonly T[], index: number, delta: number): T[] {
  return moveTo(list, index, index + delta)
}

export function sameOrder(left: readonly number[], right: readonly number[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

/**
 * The add-ons in the order a prompt offers them.
 *
 * By `sortOrder`; an add-on with no row sorts **after** every ordered one, by name - §10A.364's own rule, so the
 * list on this screen is the list the customer sees.
 */
export function itemsInPromptOrder(
  items: readonly UpsellItem[],
  nameOf: (productId: number) => string,
): UpsellItem[] {
  return [...items].sort((left, right) => {
    if (left.sortOrder !== null && right.sortOrder !== null) {
      return left.sortOrder - right.sortOrder || left.addonProductId - right.addonProductId
    }
    if (left.sortOrder !== null) return -1
    if (right.sortOrder !== null) return 1
    return nameOf(left.addonProductId).localeCompare(nameOf(right.addonProductId), 'sk')
  })
}

export function groupsInOrder(groups: readonly UpsellGroup[]): UpsellGroup[] {
  return [...groups].sort((left, right) => left.sortOrder - right.sortOrder || left.id - right.id)
}

// ─── Links ───────────────────────────────────────────────────────────────────

/** The products this add-on is offered with **in that group** (null: the ungrouped links), ascending. */
export function linkedProductIds(item: UpsellItem | undefined, groupId: number | null): number[] {
  return (item?.links ?? [])
    .filter((link) => link.groupId === groupId)
    .map((link) => link.productId)
    .sort((left, right) => left - right)
}

/**
 * The group a product is linked to this add-on under, when it is not `groupId` - `undefined` when it is not
 * linked elsewhere.
 *
 * **One pair, one way** (§10A.364's unique index): ticking such a product here **moves** it into this group, so
 * the checklist says where it is now rather than letting the save do something nobody was told about.
 */
export function elsewhereGroupOf(
  item: UpsellItem | undefined,
  productId: number,
  groupId: number | null,
): number | null | undefined {
  const link = (item?.links ?? []).find((one) => one.productId === productId)
  if (link === undefined || link.groupId === groupId) return undefined
  return link.groupId
}

/**
 * The add-ons this product has **under a group** - §9.719. The product dialog leaves these to *Upsell produktov*:
 * `PUT /products/{id}/addons` replaces the ungrouped links only (§10A.365), so a grouped one drawn there could be
 * unticked and saved and stay exactly where it was.
 *
 * Read off `GET /products/{id}/addon-groups` since §9.744 amended (BACKEND.md §10A.397) - the products side, so a role
 * without `upsell` is told too - where it was worked out from the upsell screen's whole list. Ascending and distinct,
 * so the dialog names them in a stable order; an entry that is not a whole id is left out rather than named.
 */
export function groupedAddonIdsOf(
  answer: { addonProductIds?: readonly unknown[] | null } | undefined,
): number[] {
  const ids = (answer?.addonProductIds ?? []).filter(
    (id): id is number => typeof id === 'number' && Number.isInteger(id),
  )
  return [...new Set(ids)].sort((left, right) => left - right)
}

/** The links one item has under a group - how many detach when that group is deleted. */
export function linksInGroup(items: readonly UpsellItem[], groupId: number): number {
  return items.reduce(
    (total, item) => total + item.links.filter((link) => link.groupId === groupId).length,
    0,
  )
}

/** The add-ons offered under a group, each with how many products - the group's own list. */
export function itemsOfGroup(
  items: readonly UpsellItem[],
  groupId: number,
): { addonProductId: number; products: number }[] {
  return items
    .map((item) => ({
      addonProductId: item.addonProductId,
      products: item.links.filter((link) => link.groupId === groupId).length,
    }))
    .filter((entry) => entry.products > 0)
}

/** The save body: exactly the ticked products, ascending and without repeats. */
export function linksPayloadOf(
  groupId: number | null,
  ticked: ReadonlySet<number>,
): UpsellLinksPayload {
  return { groupId, productIds: [...ticked].sort((left, right) => left - right) }
}

// ─── The category checklist ──────────────────────────────────────────────────

export interface ChecklistGroup {
  /** Stable for React and for the e2e: the category's id, or `none`. */
  key: string
  category: string
  products: Product[]
}

/**
 * Lower case and without diacritics, so *sunka* finds *Šunka* - a search box on a till is typed fast and
 * rarely with the háček.
 */
export function folded(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

/**
 * The products under their menu category, **in the menu's category order** - the order the operator set on
 * *Kategórie*, so the checklist reads like the menu they are thinking of.
 *
 * Categories by `sortOrder` then name, products by name, *Nezaradené* last (it is the absence of a category, not
 * one of them). `excluded` leaves out the add-on itself - a product is not its own upsell - and the copy's source.
 * The search matches the product's name only, diacritics ignored; a category with nothing matching is left out.
 */
export function checklistGroups(
  products: readonly Product[] | undefined,
  categories: readonly Category[] | undefined,
  search: string,
  excluded: ReadonlySet<number>,
): ChecklistGroup[] {
  const query = folded(search.trim())
  const byId = new Map<number, Category>()
  for (const category of categories ?? []) {
    if (category.id !== undefined) byId.set(category.id, category)
  }

  const buckets = new Map<string, { category: string; rank: number; products: Product[] }>()
  for (const product of products ?? []) {
    if (product.id === undefined || excluded.has(product.id)) continue
    if (query !== '' && !folded(product.name ?? '').includes(query)) continue

    const known = product.categoryId === undefined ? undefined : byId.get(product.categoryId)
    const fallbackName = (product.categoryName ?? '').trim()
    const key =
      known?.id !== undefined
        ? String(known.id)
        : product.categoryId !== undefined && fallbackName !== ''
          ? String(product.categoryId)
          : 'none'
    const name = key === 'none' ? UNCATEGORISED_ADDONS : (known?.name ?? fallbackName)
    const rank = key === 'none' ? Number.MAX_SAFE_INTEGER : (known?.sortOrder ?? Number.MAX_SAFE_INTEGER - 1)
    const bucket = buckets.get(key)
    if (bucket) bucket.products.push(product)
    else buckets.set(key, { category: name, rank, products: [product] })
  }

  return [...buckets.entries()]
    .map(([key, bucket]) => ({
      key,
      category: bucket.category,
      rank: bucket.rank,
      products: [...bucket.products].sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '', 'sk'),
      ),
    }))
    .sort((left, right) => left.rank - right.rank || left.category.localeCompare(right.category, 'sk'))
    .map(({ key, category, products: entries }) => ({ key, category, products: entries }))
}

/** The ids a checklist group offers, for *všetky* and *žiadne*. */
export function idsOf(group: ChecklistGroup): number[] {
  return group.products.flatMap((product) => (product.id === undefined ? [] : [product.id]))
}

/** *Všetky* - every product of the category ticked, the rest of the selection kept. */
export function withAll(selected: ReadonlySet<number>, ids: readonly number[]): Set<number> {
  return new Set([...selected, ...ids])
}

/** *Žiadne* - every product of the category unticked, the rest of the selection kept. */
export function withNone(selected: ReadonlySet<number>, ids: readonly number[]): Set<number> {
  const removed = new Set(ids)
  return new Set([...selected].filter((id) => !removed.has(id)))
}

export function toggled(selected: ReadonlySet<number>, id: number): Set<number> {
  const next = new Set(selected)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/** How many of these ids are ticked - the per-category count beside *všetky / žiadne*. */
export function tickedCount(selected: ReadonlySet<number>, ids: readonly number[]): number {
  return ids.filter((id) => selected.has(id)).length
}

export function sameSet(left: ReadonlySet<number>, right: ReadonlySet<number>): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id))
}

// ─── Groups ──────────────────────────────────────────────────────────────────

/** The group form as typed - strings, because an empty box means something different from nought. */
export interface GroupDraft {
  name: string
  maxPerUnit: string
  price: string
  active: boolean
}

/** Cents as a box shows them: `150` -> `1,50`. Built by hand because `toFixed` is refused in this tree (N-02). */
export function centsInput(cents: number): string {
  const whole = Math.trunc(cents / 100)
  const rest = Math.abs(cents % 100)
  return `${String(whole)},${String(rest).padStart(2, '0')}`
}

export function draftOfGroup(group: UpsellGroup | null): GroupDraft {
  return {
    name: group?.name ?? '',
    maxPerUnit: group?.maxPerUnit == null ? '' : String(group.maxPerUnit),
    price: group?.priceCents == null ? '' : centsInput(group.priceCents),
    active: group?.active ?? true,
  }
}

const PRICE = /^\d+(?:[.,]\d{1,2})?$/
const WHOLE = /^\d+$/

/**
 * Why this draft cannot be saved, or null.
 *
 * **An empty price box is the item's own price and an empty limit is no limit** - so neither may be parsed,
 * because `Number('')` is nought and nought is *free* (`agos-an-empty-string-parses-as-zero`). A typed `0` price
 * is free and is accepted; a typed `0` limit is refused, since the server refuses it too.
 */
export function groupDraftProblem(draft: GroupDraft): string | null {
  if (draft.name.trim() === '') return tt('Skupina musí mať názov.')
  const limit = draft.maxPerUnit.trim()
  if (limit !== '' && (!WHOLE.test(limit) || Number(limit) < 1)) {
    return tt('Vybrať najviac musí byť celé číslo 1 alebo viac, alebo prázdne pre bez obmedzenia.')
  }
  const price = draft.price.trim()
  if (price !== '' && !PRICE.test(price)) {
    return tt('Cena v skupine musí byť suma v eurách, napríklad 1,50, alebo prázdna pre vlastnú cenu doplnku.')
  }
  return null
}

/** The body for `POST`/`PATCH /upsell/groups`, or null when {@link groupDraftProblem} refuses the draft. */
export function groupPayloadOf(draft: GroupDraft): UpsellGroupPayload | null {
  if (groupDraftProblem(draft) !== null) return null
  const limit = draft.maxPerUnit.trim()
  const price = draft.price.trim()
  return {
    name: draft.name.trim(),
    maxPerUnit: limit === '' ? null : Number(limit),
    priceCents: price === '' ? null : parseMoneyToCents(price),
    active: draft.active,
  }
}

/** A group's price as the screen says it: the item's own, *zadarmo*, or the amount. */
export function groupPriceLabel(priceCents: number | null): string {
  if (priceCents === null) return tt('vlastná cena doplnku')
  if (priceCents === 0) return tt('zadarmo')
  return formatCents(priceCents)
}

/** A group's limit as the screen says it, per unit of the product (#133). */
export function groupLimitLabel(maxPerUnit: number | null): string {
  if (maxPerUnit === null) return tt('bez obmedzenia')
  return `${tt('najviac')} ${formatCount(maxPerUnit)} ${tt('na kus')}`
}

// ─── Copy ────────────────────────────────────────────────────────────────────

/** Every link the source product has: each add-on it is offered, with its group. */
export function linksOfProduct(
  items: readonly UpsellItem[],
  productId: number,
): { addonProductId: number; groupId: number | null }[] {
  return items.flatMap((item) =>
    item.links
      .filter((link) => link.productId === productId)
      .map((link) => ({ addonProductId: item.addonProductId, groupId: link.groupId })),
  )
}

export interface CopySummary {
  /** Target products, the source left out. */
  products: number
  /** How many links the source has - what each target receives. */
  sourceLinks: number
  /** Links a target does not have yet. */
  added: number
  /** Links a target has under another group, which take the source's group (one pair, one way). */
  regrouped: number
  /** `nahradiť` only: the targets' own links, removed first. */
  removed: number
}

/** The targets, without the source and without repeats, ascending - what `POST /upsell/copy` is sent. */
export function copyTargets(fromProductId: number, toProductIds: Iterable<number>): number[] {
  return [...new Set(toProductIds)]
    .filter((id) => id !== fromProductId)
    .sort((left, right) => left - right)
}

/**
 * What a copy will touch, counted before it runs - §9.711's *saying how many products and links it will touch
 * before it does*.
 *
 * `pridať` adds what a target lacks and moves what it has under another group; `nahradiť` first removes every
 * link the target has, then adds all the source's. A target equal to the source is skipped, as the server does.
 */
export function copySummary(
  items: readonly UpsellItem[],
  fromProductId: number,
  toProductIds: Iterable<number>,
  mode: CopyMode,
): CopySummary {
  const source = linksOfProduct(items, fromProductId)
  const targets = copyTargets(fromProductId, toProductIds)
  let added = 0
  let regrouped = 0
  let removed = 0
  for (const target of targets) {
    const own = linksOfProduct(items, target)
    if (mode === 'replace') {
      removed += own.length
      added += source.length
      continue
    }
    for (const link of source) {
      const existing = own.find((one) => one.addonProductId === link.addonProductId)
      if (existing === undefined) added += 1
      else if (existing.groupId !== link.groupId) regrouped += 1
    }
  }
  return { products: targets.length, sourceLinks: source.length, added, regrouped, removed }
}

export function copyPayloadOf(
  fromProductId: number,
  toProductIds: Iterable<number>,
  mode: CopyMode,
): UpsellCopyPayload {
  return { fromProductId, toProductIds: copyTargets(fromProductId, toProductIds), mode }
}

/**
 * The confirmation's lines, from the summary - each a label and a count, so a translator gets whole phrases
 * rather than sentence fragments glued round numbers.
 *
 * The destructive line comes first under `nahradiť`, because *removes* is the part somebody must not miss.
 */
export function copyLines(summary: CopySummary, mode: CopyMode): string[] {
  const lines = [`${tt('Cieľových produktov')}: ${formatCount(summary.products)}`]
  if (mode === 'replace') {
    lines.push(`${tt('Odstráni sa ich vlastných väzieb')}: ${formatCount(summary.removed)}`)
    lines.push(`${tt('Pridá sa väzieb zo zdroja')}: ${formatCount(summary.added)}`)
    return lines
  }
  lines.push(`${tt('Pridá sa nových väzieb')}: ${formatCount(summary.added)}`)
  if (summary.regrouped > 0) {
    lines.push(
      `${tt('Existujúcich väzieb, ktoré dostanú skupinu zo zdroja')}: ${formatCount(summary.regrouped)}`,
    )
  }
  return lines
}
